/* ============================================================
   48-Hour Data Readiness

   What an operator must have in hand before a diagnostic is worth
   running. The value is in the honesty of the gaps: an engagement that
   starts without these spends its first week doing archaeology, and
   the client pays for that week.

   Items are weighted — "blocking" items stop the diagnostic outright,
   "improves" items only reduce confidence.
   ============================================================ */

import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { session } from '../../shared/storage.js';
import { percent } from '../../shared/format.js';
import { PrintButton } from '../../components/PrintButton.js';
import { readable } from '../../shared/tone.js';

const STORAGE_KEY = 'readiness_progress';

const SECTIONS = [
  {
    id: 'financial',
    name: 'Financial',
    items: [
      { id: 'pl', label: 'Management accounts for the last 12 months', weight: 'blocking' },
      { id: 'fleet-cost', label: 'Cost per vehicle per month, or per lane', weight: 'blocking' },
      { id: 'fuel', label: 'Fuel purchase records with litres, not just value', weight: 'blocking' },
      { id: 'debtors', label: 'Debtor ageing — who owes what, and for how long', weight: 'improves' },
      { id: 'financing', label: 'Loan and lease schedules with rates and terms', weight: 'improves' }
    ]
  },
  {
    id: 'operational',
    name: 'Operational',
    items: [
      { id: 'trips', label: 'Trip log for the last 3 months: lane, date, tonnage, revenue', weight: 'blocking' },
      { id: 'empty', label: 'Loaded versus empty kilometres', weight: 'blocking' },
      { id: 'fleet-list', label: 'Fleet register: registration, make, year, capacity', weight: 'blocking' },
      { id: 'downtime', label: 'Breakdown and downtime log', weight: 'improves' },
      { id: 'telematics', label: 'Telematics export, if fitted', weight: 'improves' }
    ]
  },
  {
    id: 'customs',
    name: 'Customs & port',
    items: [
      { id: 'entries', label: 'Customs entries for the last 3 months', weight: 'blocking' },
      { id: 'dwell', label: 'Container gate-in and gate-out timestamps', weight: 'blocking' },
      { id: 'demurrage', label: 'Demurrage and detention invoices', weight: 'blocking' },
      { id: 'channel', label: 'Green / yellow / red channel split', weight: 'improves' },
      { id: 'agents', label: 'List of clearing agents used, with licence numbers', weight: 'improves' }
    ]
  },
  {
    id: 'compliance',
    name: 'Compliance',
    items: [
      { id: 'licences', label: 'All current licences with expiry dates', weight: 'blocking' },
      { id: 'insurance', label: 'Insurance schedules: cargo, fleet, liability', weight: 'improves' },
      { id: 'incidents', label: 'Incident and accident register', weight: 'improves' },
      { id: 'drivers', label: 'Driver register with licence classes and expiry', weight: 'improves' }
    ]
  },
  {
    id: 'commercial',
    name: 'Commercial',
    items: [
      { id: 'contracts', label: 'Customer contracts with rate schedules', weight: 'blocking' },
      { id: 'concentration', label: 'Revenue by customer — the concentration picture', weight: 'improves' },
      { id: 'pipeline', label: 'Quoted but unwon business from the last 6 months', weight: 'improves' }
    ]
  }
];

const ALL_ITEMS = SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, sectionId: s.id })));
const BLOCKING_COUNT = ALL_ITEMS.filter((i) => i.weight === 'blocking').length;

export function ReadinessPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>48-Hour Data Readiness</h1>
      <p>
        What has to be in hand before a diagnostic is worth running. Blocking items stop the
        work; the rest only cost you confidence. Your ticks are saved on this device.
      </p>
    </header>
    <div id="readiness-body"></div>
  `;

  const body = page.querySelector('#readiness-body');
  // The list is rebuilt on every tick, which would otherwise drop keyboard
  // focus back to the top of the document. Carry the focused item through.
  const paint = (focusId) => render(body, paint, focusId);
  paint();

  return page;
}

function getState() {
  return session.get(STORAGE_KEY, {}) || {};
}

function render(container, repaint, focusId = null) {
  const state = getState();
  const have = (item) => Boolean(state[item.id]);

  const blockingHave = ALL_ITEMS.filter((i) => i.weight === 'blocking' && have(i)).length;
  const totalHave = ALL_ITEMS.filter(have).length;
  const readyPct = Math.round((blockingHave / BLOCKING_COUNT) * 100);
  const ready = blockingHave === BLOCKING_COUNT;

  container.replaceChildren();

  const summary = document.createElement('div');
  summary.id = 'readiness-summary';
  const color = ready ? 'var(--ca-positive)' : readyPct >= 60 ? 'var(--ca-warning)' : 'var(--ca-danger)';

  summary.innerHTML = html`
    <div class="card avoid-break" style="margin-bottom: var(--ca-space-5); border-color: color-mix(in srgb, ${color} 30%, var(--ca-border));">
      <div class="row" style="margin-bottom: var(--ca-space-3);">
        <span style="color:${readable(color)};">${raw(icon(ready ? 'check' : 'alert', 18))}</span>
        <h2 style="flex:1; font-size: var(--ca-text-base);">
          ${ready ? 'Ready to run' : 'Not ready yet'}
        </h2>
        <span class="badge num" style="background: color-mix(in srgb, ${color} 12%, transparent); color:${readable(color)};">
          ${percent(readyPct)}
        </span>
      </div>
      <div class="progress-track" style="margin-bottom: var(--ca-space-3);">
        <div class="progress-fill" style="width:${readyPct}%; background:${color};"></div>
      </div>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary);">
        <span class="num">${blockingHave}</span> of <span class="num">${BLOCKING_COUNT}</span>
        blocking items in hand · <span class="num">${totalHave}</span> of
        <span class="num">${ALL_ITEMS.length}</span> items overall.
        ${ready
          ? ' Every blocking item is covered — the diagnostic can start.'
          : ' The missing blocking items below are what a first week would otherwise be spent chasing.'}
      </p>
    </div>
  `;
  container.appendChild(summary);

  const detail = document.createElement('div');
  detail.id = 'readiness-detail';

  for (const section of SECTIONS) {
    const sectionHave = section.items.filter(have).length;

    const card = document.createElement('section');
    card.className = 'card avoid-break';
    card.style.marginBottom = 'var(--ca-space-3)';
    card.innerHTML = html`
      <div class="row" style="margin-bottom: var(--ca-space-3);">
        <h3 style="flex:1; font-size: var(--ca-text-sm);">${section.name}</h3>
        <span class="badge num" style="background: var(--ca-surface-sunk); color: var(--ca-text-tertiary);">
          ${sectionHave}/${section.items.length}
        </span>
      </div>
      <div class="stack" style="gap: var(--ca-space-2);" data-items></div>
    `;

    const list = card.querySelector('[data-items]');
    for (const item of section.items) {
      const checked = have(item);
      const blocking = item.weight === 'blocking';

      const label = document.createElement('label');
      label.className = 'row';
      label.dataset.item = item.id;
      label.style.cssText =
        'align-items: flex-start; gap: var(--ca-space-3); cursor: pointer; padding: var(--ca-space-1) 0;';
      label.innerHTML = html`
        <input type="checkbox" ${checked ? 'checked' : ''}
               style="margin-top: 3px; width: 17px; height: 17px; accent-color: var(--brand-accent);" />
        <span style="flex:1; font-size: var(--ca-text-sm);
                     color: ${readable(checked ? 'var(--ca-text-quaternary)' : 'var(--ca-text-secondary)')};
                     text-decoration: ${checked ? 'line-through' : 'none'};">
          ${item.label}
        </span>
        ${blocking && !checked
          ? html`<span class="badge" style="background: color-mix(in srgb, var(--ca-danger) 12%, transparent); color: var(--ca-danger);">
              Blocking
            </span>`
          : ''}
      `;

      label.querySelector('input').addEventListener('change', (event) => {
        const next = getState();
        if (event.target.checked) next[item.id] = true;
        else delete next[item.id];
        session.set(STORAGE_KEY, next);
        repaint(item.id);
      });

      list.appendChild(label);
    }

    detail.appendChild(card);
  }

  container.appendChild(detail);

  const actions = document.createElement('div');
  actions.className = 'no-print';
  actions.appendChild(
    PrintButton({
      targetId: 'readiness-body',
      title: '48-Hour Data Readiness',
      subtitle: `${blockingHave}/${BLOCKING_COUNT} blocking items in hand · ${readyPct}% ready`
    })
  );

  const reset = document.createElement('div');
  reset.style.cssText = 'text-align:center; margin-top: var(--ca-space-3);';
  const resetBtn = document.createElement('button');
  resetBtn.className = 'btn btn-ghost';
  resetBtn.type = 'button';
  resetBtn.textContent = 'Clear all ticks';
  resetBtn.addEventListener('click', () => {
    session.remove(STORAGE_KEY);
    repaint();
  });
  reset.appendChild(resetBtn);
  actions.appendChild(reset);

  container.appendChild(actions);

  if (focusId) {
    const restored = container.querySelector(`[data-item="${CSS.escape(focusId)}"] input`);
    if (restored) restored.focus({ preventScroll: true });
  }
}
